import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt, FaSync, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingSpinner from '../LoadingSpinner';
import ErrorBanner from '../UI/ErrorBanner';
import IconButton from '../UI/IconButton';
import {
  useLiveDefensiveContributions,
  DefensivePlayerStat,
  LiveFixtureDefensive,
} from '../../hooks/useLiveDefensiveContributions';

const POSITION_META: Record<number, { label: string; bg: string; text: string; ring: string }> = {
  1: { label: 'GK',  bg: 'bg-amber-100 dark:bg-amber-600/20',   text: 'text-amber-800 dark:text-amber-300',   ring: 'ring-amber-300 dark:ring-amber-500/40'  },
  2: { label: 'DEF', bg: 'bg-blue-100 dark:bg-blue-600/20',     text: 'text-blue-800 dark:text-blue-300',     ring: 'ring-blue-300 dark:ring-blue-500/40'   },
  3: { label: 'MID', bg: 'bg-purple-100 dark:bg-purple-600/20', text: 'text-purple-800 dark:text-purple-300', ring: 'ring-purple-300 dark:ring-purple-500/40' },
  4: { label: 'FWD', bg: 'bg-pink-100 dark:bg-pink-600/20',     text: 'text-pink-800 dark:text-pink-300',     ring: 'ring-pink-300 dark:ring-pink-500/40'   },
};

const ptsClass = (pts: number) =>
  pts > 0 ? 'text-emerald-700 dark:text-emerald-400 font-bold' : pts < 0 ? 'text-red-600 dark:text-red-400 font-bold' : 'text-content-muted';

const formatKickoff = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const PositionBadge: React.FC<{ type: number }> = ({ type }) => {
  const meta = POSITION_META[type] ?? POSITION_META[4];
  return (
    <span
      className={`inline-block text-2xs font-semibold px-1.5 py-0.5 rounded ${meta.bg} ${meta.text} ring-1 ${meta.ring}`}
    >
      {meta.label}
    </span>
  );
};

const StatusBadge: React.FC<{ fixture: LiveFixtureDefensive }> = ({ fixture }) => {
  const { started, finished, finishedProvisional, kickoffTime } = fixture;
  if (!started) {
    return (
      <span className="text-xs text-content-muted bg-surface-sunken rounded-full px-2.5 py-1 whitespace-nowrap">
        KO {formatKickoff(kickoffTime)}
      </span>
    );
  }
  if (finished || finishedProvisional) {
    return (
      <span className="text-xs font-bold text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-500/15 ring-1 ring-blue-300 dark:ring-blue-500/40 rounded-full px-2.5 py-1">
        FT
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-green-800 dark:text-green-400 bg-green-100 dark:bg-green-500/15 ring-1 ring-green-300 dark:ring-green-500/40 rounded-full px-2.5 py-1">
      <span className="w-1.5 h-1.5 rounded-full bg-green-600 dark:bg-green-400 animate-pulse" />
      LIVE
    </span>
  );
};

const GkRow: React.FC<{ player: DefensivePlayerStat }> = ({ player }) => {
  const { name, teamShortName, saves, penaltiesSaved } = player;
  const savePts = Math.floor(saves / 3) + penaltiesSaved * 5;

  return (
    <tr className="border-b border-line hover:bg-surface-sunken/60 transition-colors last:border-b-0">
      <td className="py-2 pl-3 pr-2">
        <PositionBadge type={1} />
      </td>
      <td className="py-2 px-2 text-sm font-medium text-content whitespace-nowrap">{name}</td>
      <td className="py-2 px-2 text-xs text-content-muted">{teamShortName}</td>
      <td className="py-2 px-2 text-xs text-content-muted tabular-nums whitespace-nowrap">
        {saves} save{saves !== 1 ? 's' : ''}
        {penaltiesSaved > 0 && <span className="text-purple-700 dark:text-purple-300 ml-1">+pen</span>}
      </td>
      <td className="py-2 pr-3 pl-2 text-right text-sm tabular-nums">
        <span className={ptsClass(savePts)}>
          {savePts > 0 ? `+${savePts}` : savePts}
        </span>
      </td>
    </tr>
  );
};

const DEFCON_THRESHOLD: Partial<Record<number, number>> = {
  2: 10, // DEF
  3: 12, // MID
  4: 12, // FWD
};

// Shared cell content used in both regular and animated rows
const OutfieldCells: React.FC<{ player: DefensivePlayerStat; rank: number }> = ({ player, rank }) => {
  const { elementType, name, teamShortName, defensiveScore } = player;
  const threshold = DEFCON_THRESHOLD[elementType];
  const fplPts = threshold !== undefined ? Math.floor(defensiveScore / threshold) : 0;
  const earnsPts = fplPts > 0;

  return (
    <>
      <td className="py-2 pl-3 pr-1 text-xs text-content-muted w-5">{rank}</td>
      <td className="py-2 px-1 whitespace-nowrap">
        <PositionBadge type={elementType} />
      </td>
      <td className="py-2 px-2 text-sm font-medium text-content whitespace-nowrap">{name}</td>
      <td className="py-2 px-2 text-xs text-content-muted">{teamShortName}</td>
      <td className="py-2 pr-3 pl-2 text-right text-sm tabular-nums font-bold">
        {threshold !== undefined ? (
          earnsPts ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-emerald-700 dark:text-emerald-400">{defensiveScore}</span>
              <span className="text-2xs font-semibold bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-400 ring-1 ring-emerald-300 dark:ring-emerald-500/30 rounded px-1 py-0.5 leading-none">
                + 2 pts
              </span>
            </span>
          ) : (
            <span className="text-red-600 dark:text-red-400">{defensiveScore}</span>
          )
        ) : (
          <span className={defensiveScore > 0 ? 'text-emerald-700 dark:text-premier-green' : 'text-content-muted'}>
            {defensiveScore}
          </span>
        )}
      </td>
    </>
  );
};

const ROW_CLASS = 'border-b border-line hover:bg-surface-sunken/60 transition-colors last:border-b-0';

const OutfieldRow: React.FC<{ player: DefensivePlayerStat; rank: number }> = ({ player, rank }) => (
  <tr className={ROW_CLASS}>
    <OutfieldCells player={player} rank={rank} />
  </tr>
);

const VISIBLE_COUNT = 5;

const rowVariants = {
  hidden: { opacity: 0, y: -6 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { duration: 0.18, delay: i * 0.04 } }),
};

const FixtureCard: React.FC<{ fixture: LiveFixtureDefensive }> = ({ fixture }) => {
  const [showAll, setShowAll] = useState(false);
  const {
    homeTeam,
    awayTeam,
    homeScore,
    awayScore,
    started,
    finished,
    finishedProvisional,
    goalkeepers,
    outfieldPlayers,
  } = fixture;
  const isLive = started && !finished && !finishedProvisional;

  const topPlayers = outfieldPlayers.slice(0, VISIBLE_COUNT);
  const extraPlayers = outfieldPlayers.slice(VISIBLE_COUNT);
  const hasMore = extraPlayers.length > 0;

  return (
    <div
      className={`bg-surface rounded-xl overflow-hidden flex flex-col ${
        isLive
          ? 'ring-2 ring-green-500/50 shadow-lg shadow-green-500/20'
          : 'ring-1 ring-line'
      }`}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b border-line ${isLive ? 'bg-green-500/5' : ''}`}>
        <div className="flex items-center gap-3">
          <StatusBadge fixture={fixture} />
          <div className="flex items-center gap-2 flex-1 min-w-0 text-sm font-semibold">
            <span className="flex-1 text-right truncate">{homeTeam.name}</span>
            <span className="text-lg font-bold text-content tabular-nums shrink-0">
              {started ? `${homeScore ?? 0} – ${awayScore ?? 0}` : 'vs'}
            </span>
            <span className="flex-1 text-left truncate">{awayTeam.name}</span>
          </div>
        </div>
      </div>

      {started ? (
        <div className="flex flex-col divide-y divide-line">
          {/* Goalkeepers section */}
          {goalkeepers.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-2xs uppercase tracking-widest text-amber-700 dark:text-amber-300/80 font-semibold">
                Goalkeepers · saves → pts
              </div>
              <table className="w-full">
                <tbody>
                  {goalkeepers.map(p => <GkRow key={p.id} player={p} />)}
                </tbody>
              </table>
            </div>
          )}

          {/* Outfield section */}
          {outfieldPlayers.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-2xs uppercase tracking-widest text-blue-700 dark:text-blue-300/80 font-semibold">
                Outfield · defensive actions
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-2xs uppercase tracking-wide text-content-muted border-b border-line">
                    <th className="py-1 pl-3 pr-1 text-left w-5">#</th>
                    <th className="py-1 px-1 text-left">Pos</th>
                    <th className="py-1 px-2 text-left">Player</th>
                    <th className="py-1 px-2 text-left">Team</th>
                    <th className="py-1 pr-3 pl-2 text-right">DefCon</th>
                  </tr>
                </thead>
                <tbody>
                  {topPlayers.map((p, i) => (
                    <OutfieldRow key={p.id} player={p} rank={i + 1} />
                  ))}
                </tbody>
                <AnimatePresence>
                  {showAll && (
                    <motion.tbody>
                      {extraPlayers.map((p, i) => (
                        <motion.tr
                          key={p.id}
                          className={ROW_CLASS}
                          custom={i}
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
                        >
                          <OutfieldCells player={p} rank={VISIBLE_COUNT + i + 1} />
                        </motion.tr>
                      ))}
                    </motion.tbody>
                  )}
                </AnimatePresence>
              </table>

              {hasMore && (
                <button
                  onClick={() => setShowAll(v => !v)}
                  className="w-full min-h-11 flex items-center justify-center gap-1.5 py-2.5 text-xs text-content-muted hover:text-content transition-colors border-t border-line"
                >
                  <motion.span
                    animate={{ rotate: showAll ? 180 : 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                  >
                    <FaChevronDown className="text-2xs" />
                  </motion.span>
                  {showAll ? 'Show less' : `Show ${extraPlayers.length} more`}
                </button>
              )}
            </div>
          )}

          {goalkeepers.length === 0 && outfieldPlayers.length === 0 && (
            <div className="py-5 text-center text-sm text-content-muted">No player data yet</div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center px-4 py-8 text-center text-sm text-content-muted">
          Stats available once the match kicks off
        </div>
      )}
    </div>
  );
};

const LiveDefensiveContributions: React.FC = () => {
  const navigate = useNavigate();
  const { fixtures, currentEvent, loading, error, refresh } = useLiveDefensiveContributions();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40dvh] gap-3 text-content">
        <LoadingSpinner />
        <span className="text-content-secondary text-sm">Loading defensive contributions…</span>
      </div>
    );
  }

  if (error) {
    return <ErrorBanner message={error} className="mx-4" />;
  }

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-4 py-4 text-content">
      {/* Page header */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <IconButton label="Go back" onClick={() => navigate(-1)}>
            <FaArrowLeft />
          </IconButton>
          <div className="flex items-center gap-2.5">
            <FaShieldAlt className="text-emerald-700 dark:text-premier-green text-xl shrink-0" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">
                Defensive Contributions
              </h1>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {currentEvent && (
            <span className="px-3 py-1 rounded-full bg-surface-raised border border-line text-sm">
              GW {currentEvent}
            </span>
          )}
          <IconButton label="Refresh data" onClick={refresh}>
            <FaSync className="text-sm" />
          </IconButton>
        </div>
      </div>

      {/* Key */}
      <div className="mb-5 flex flex-wrap gap-x-5 gap-y-1 text-2xs text-content-secondary bg-surface rounded-xl px-4 py-2.5 ring-1 ring-line">
        <span><span className="text-amber-700 dark:text-amber-300">GK</span> · saves → FPL pts (every 3 saves = <span className="text-emerald-700 dark:text-emerald-400 font-semibold">+1</span>, pen save = <span className="text-emerald-700 dark:text-emerald-400 font-semibold">+5</span>)</span>
        <span><span className="text-blue-700 dark:text-blue-300">Outfield</span> · DefCon = official FPL defensive contribution · <span className="text-emerald-700 dark:text-emerald-400">green</span> = threshold met (+2 FPL pt per <span className="text-blue-700 dark:text-blue-300">DEF</span> 10 / <span className="text-purple-700 dark:text-purple-300">MID·FWD</span> 12) · <span className="text-red-600 dark:text-red-400">red</span> = below threshold</span>
      </div>

      {/* Fixture grid */}
      {fixtures.length === 0 ? (
        <div className="bg-surface rounded-xl p-8 text-center text-content-muted">
          No fixtures found for this gameweek.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fixtures.map((fixture) => (
            <FixtureCard key={fixture.fixtureId} fixture={fixture} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveDefensiveContributions;
